import { useState, useEffect } from 'react';

import type { Site } from '@hotjar/site';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';
import { noop } from '@hotjar/webapp/lib/function';

interface UseTargetingSettings {
  isLoading: boolean;
  sessionIncludeShort: boolean;
  targetingRulesPresent: boolean;
  targetingRulesWithPageRulePresent: boolean;
  targetingRulesWithJsTriggerPresent: boolean;
  sessionCaptureConsoleConsent: boolean;
}

export function useTargetingSettings(site: Site | undefined): UseTargetingSettings {
  const [isLoading, setIsLoading] = useState(false);
  const [sessionIncludeShort, setSessionIncludeShort] = useState(false);
  const [sessionCaptureConsoleConsent, setSessionCaptureConsoleConsent] = useState(false);
  const [targetingRulesPresent, setTargetingRulesPresent] = useState(false);
  const [targetingRulesWithJsTriggerPresent, setTargetingRulesWithJsTriggerPresent] =
    useState(false);
  const [targetingRulesWithPageRulePresent, setTargetingRulesWithPageRulePresent] = useState(false);

  useEffect(() => {
    if (!site) {
      return noop;
    }

    let isCanceled = false;

    setIsLoading(true);
    (async () => {
      const response = await sessionsSettingsApi.get(site.id);
      if (response.success && !isCanceled) {
        const sessionTargetingRules = response.data?.session_targeting_rules || [];
        setSessionCaptureConsoleConsent(response.data?.session_capture_console_consent === true);
        setSessionIncludeShort(!response.data?.session_exclude_short);
        setTargetingRulesPresent(!!sessionTargetingRules.length);
        setTargetingRulesWithJsTriggerPresent(
          sessionTargetingRules.some((item) => item.component === 'trigger'),
        );
        setTargetingRulesWithPageRulePresent(
          sessionTargetingRules.some((item) => item.component === 'url'),
        );
      }
      setIsLoading(false);
    })();

    return function cleanup() {
      isCanceled = true;
    };
  }, [site]);

  return {
    isLoading,
    sessionIncludeShort,
    targetingRulesPresent,
    targetingRulesWithPageRulePresent,
    targetingRulesWithJsTriggerPresent,
    sessionCaptureConsoleConsent,
  };
}
